import { CreditCard as Edit, Trash2, CheckCircle } from 'lucide-react';
import type { AlertResume, AlertType } from '../../types/alertResume';
import { Button, EmptyState, LoadingSpinner, Badge } from '../ui';

function formatDateTimeRaw(raw: string): string {
  // Parse directly from the string to avoid any timezone conversion.
  // Handles "2024-01-15T10:30:45..." and "2024-01-15 10:30:45..." formats.
  const normalized = raw.replace('T', ' ');
  const [datePart, timePart = ''] = normalized.split(' ');
  const [year, month, day] = datePart.split('-');
  const [hh, mm, ss = '00'] = timePart.split(':');
  const seconds = ss.replace(/\.\d+Z?$/, '').replace('Z', '');
  return `${day}/${month}/${year} ${hh}:${mm}:${seconds}`;
}

interface AlertResumeListProps {
  resumes: AlertResume[];
  onEdit: (resume: AlertResume) => void;
  onDelete: (resume: AlertResume) => void;
  onResolve: (resume: AlertResume) => void;
  isLoading: boolean;
  isFiltered: boolean;
}

const ALERT_TYPE_LABELS: Record<AlertType, string> = {
  downdetector: 'Downdetector',
  manual: 'Manual',
  erp: 'ERP',
  resumo: 'Resumo',
};

const ALERT_TYPE_VARIANTS: Record<AlertType, 'default' | 'warning' | 'secondary' | 'info'> = {
  downdetector: 'warning',
  manual: 'default',
  erp: 'secondary',
  resumo: 'info',
};

function AlertResumeList({
  resumes,
  onEdit,
  onDelete,
  onResolve,
  isLoading,
  isFiltered,
}: AlertResumeListProps) {
  if (isLoading) {
    return (
      <div className="flex justify-center items-center py-12">
        <LoadingSpinner size="lg" />
      </div>
    );
  }

  if (resumes.length === 0) {
    return (
      <EmptyState
        title={isFiltered ? 'Nenhum alerta encontrado' : 'Nenhum alerta cadastrado'}
        description={
          isFiltered
            ? 'Tente ajustar os filtros para ver mais resultados.'
            : 'Comece criando seu primeiro resumo de alerta.'
        }
      />
    );
  }

  return (
    <div className="bg-white dark:bg-gray-800 shadow-sm rounded-lg overflow-hidden">
      <div className="overflow-x-auto">
        <table className="min-w-full divide-y divide-gray-200 dark:divide-gray-700">
          <thead className="bg-gray-50 dark:bg-gray-900">
            <tr>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                Resumo
              </th>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                Tipo
              </th>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                Usuário
              </th>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                Status
              </th>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                Data/Hora
              </th>
              <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                Ações
              </th>
            </tr>
          </thead>
          <tbody className="bg-white dark:bg-gray-800 divide-y divide-gray-200 dark:divide-gray-700">
            {resumes.map(resume => (
              <tr
                key={resume.id}
                className="hover:bg-gray-50 dark:hover:bg-gray-700 transition-colors"
              >
                <td className="px-6 py-4">
                  <div className="text-sm text-gray-900 dark:text-gray-100 max-w-md truncate">
                    {resume.resume}
                  </div>
                </td>
                <td className="px-6 py-4 whitespace-nowrap">
                  <Badge variant={ALERT_TYPE_VARIANTS[resume.alert_type] ?? 'default'}>
                    {ALERT_TYPE_LABELS[resume.alert_type] ?? resume.alert_type}
                  </Badge>
                </td>
                <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-700 dark:text-gray-300">
                  {resume.user_account_name ?? <span className="text-gray-400">-</span>}
                </td>
                <td className="px-6 py-4 whitespace-nowrap">
                  <Badge variant={resume.resolved ? 'success' : 'warning'}>
                    {resume.resolved ? 'Resolvido' : 'Pendente'}
                  </Badge>
                </td>
                <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500 dark:text-gray-400">
                  {formatDateTimeRaw(resume.created_at)}
                </td>
                <td className="px-6 py-4 whitespace-nowrap text-right text-sm font-medium">
                  <div className="flex justify-end gap-2">
                    {!resume.resolved && (
                      <Button
                        variant="success"
                        size="sm"
                        onClick={() => onResolve(resume)}
                        title="Marcar como resolvido"
                      >
                        <CheckCircle className="h-4 w-4" />
                      </Button>
                    )}
                    <Button
                      variant="secondary"
                      size="sm"
                      onClick={() => onEdit(resume)}
                      title="Editar"
                    >
                      <Edit className="h-4 w-4" />
                    </Button>
                    <Button
                      variant="danger"
                      size="sm"
                      onClick={() => onDelete(resume)}
                      title="Excluir"
                    >
                      <Trash2 className="h-4 w-4" />
                    </Button>
                  </div>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}

export default AlertResumeList;
